/**
 * DaggerCompactExplainer — progressive-disclosure explainer for dagger compact
 * categories.
 *
 * A dagger compact category is a compact-closed category with an involutive
 * contravariant endofunctor † that reverses morphisms. This is the categorical
 * foundation of quantum mechanics, quantum computing, and reversible
 * computation. Full dagger compact infrastructure is an active research
 * direction for CSK's ACCOUNT compositionality principle; the discharged
 * ACCOUNT path currently uses linear/compact-closed resource duality.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  DaggerCompactDiagram,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-dagger-compact",
  kind: "Categorical structure",
  title: "Dagger compact category",
  subtitle: "† : Hom(A,B) → Hom(B,A) · compact + involutive adjoint · quantum structure",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Compact-closed", href: "#explainer-compact-closed" },
  { label: "Linear category", href: "#explainer-linear-category" },
  { label: "Adjunction", href: "#explainer-adjunction" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>dagger compact category</strong> is a compact-closed category with an extra{" "}
      <em>involutive</em> functor <MathInline>{tex`\dagger`}</MathInline> that reverses morphisms (
      <MathInline>{tex`f^{\dagger} : B \to A`}</MathInline> for every{" "}
      <MathInline>{tex`f : A \to B`}</MathInline>) and is compatible with the monoidal and duality
      structure. This is the categorical foundation of quantum mechanics, quantum computing, and
      reversible computation. It is a natural extension of the compact-closed structure CSK already
      uses for <code>RES.BALANCE</code>.
    </p>
  ),
  intuition: (
    <>
      <p>
        Every process has a canonical "undo" or "adjoint" operation. In quantum mechanics, the
        dagger of a quantum gate is its inverse — applying a gate and then its dagger returns the
        system to its original state.
      </p>
      <p>
        The finite-dimensional Hilbert spaces (<code>FdHilb</code>) and the category of relations (
        <code>Rel</code>) are the archetypal examples. The dagger gives every morphism a canonical
        adjoint — and for <em>unitary</em> morphisms (those satisfying{" "}
        <MathInline>{tex`f^{\dagger} \circ f = \mathrm{id}`}</MathInline>), the adjoint is a true
        inverse.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        A <strong>dagger category</strong> <MathInline>{tex`(C, \dagger)`}</MathInline> is a
        category <MathInline>{tex`C`}</MathInline> equipped with a contravariant involutive
        identity-on-objects functor <MathInline>{tex`\dagger : C^{\mathrm{op}} \to C`}</MathInline>.
        That is, for every <MathInline>{tex`f : A \to B`}</MathInline> we get{" "}
        <MathInline>{tex`f^{\dagger} : B \to A`}</MathInline>, satisfying:
      </p>
      <ul>
        <li>
          <strong>Involution:</strong> <MathInline>{tex`(f^{\dagger})^{\dagger} = f`}</MathInline>
        </li>
        <li>
          <strong>Contravariance:</strong>{" "}
          <MathInline>{tex`(g \circ f)^{\dagger} = f^{\dagger} \circ g^{\dagger}`}</MathInline>
        </li>
      </ul>
      <DaggerCompactDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        A <strong>dagger compact category</strong> is a dagger category that is also compact closed,
        with compatibility: the dagger relates the cup and cap maps (for example,{" "}
        <MathInline>{tex`\varepsilon = \sigma \circ \eta^{\dagger}`}</MathInline> where{" "}
        <MathInline>{tex`\sigma`}</MathInline> is the swap), ensuring the monoidal and duality
        structure cohere with the involution. This extends the compact-closed resource duality that
        CSK uses for <code>RES.BALANCE</code> with coherent adjoints — a research direction for
        strengthening the resource-accounting guarantee.
      </p>
      <p>
        <strong>Unitary morphisms</strong> satisfy{" "}
        <MathInline>{tex`f^{\dagger} \circ f = \mathrm{id}`}</MathInline> and{" "}
        <MathInline>{tex`f \circ f^{\dagger} = \mathrm{id}`}</MathInline>.
      </p>
      <MathBlock ariaLabel="Unitary condition: f-dagger composed with f equals identity, and f composed with f-dagger equals identity">
        {tex`f^{\dagger} \circ f \;=\; \mathrm{id}
\qquad
f \circ f^{\dagger} \;=\; \mathrm{id}`}
      </MathBlock>
      <p>
        In the Abramsky–Coecke categorical quantum mechanics framework (2004): morphisms in{" "}
        <code>FdHilb</code> are linear maps, the dagger is the conjugate transpose, and unitary
        morphisms are exactly the quantum gates.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Dagger compact categories are a natural extension of the compact-closed resource duality
        that CSK already uses in <code>RES.BALANCE</code>. Full dagger compact infrastructure for
        the <code>ACCOUNT</code> compositionality principle is an active research direction. Where
        compact closure lets resources be minted (<MathInline>{tex`\eta`}</MathInline>) and consumed
        (<MathInline>{tex`\varepsilon`}</MathInline>), the dagger would give every resource
        transformation a canonical adjoint — and for unitary morphisms, a true inverse —
        strengthening the resource-accounting guarantee.
      </p>
      <p>Applications beyond CSK:</p>
      <ul>
        <li>
          <strong>Categorical quantum mechanics</strong> — <code>FdHilb</code> (finite-dimensional
          Hilbert spaces) is the canonical semantic dagger compact category; quantum protocols
          (teleportation, entanglement swapping) are string diagrams in this category.
        </li>
        <li>
          <strong>Quantum computing</strong> — the ZX-calculus is a presentation of a free dagger
          compact category used for quantum circuit optimization.
        </li>
        <li>
          <strong>Natural language processing</strong> — the DisCoCat model (Coecke, Sadrzadeh,
          Clark) uses <code>FdVect</code> as a dagger compact category for compositional
          distributional semantics.
        </li>
        <li>
          <strong>Topological quantum field theories</strong> — 2D TQFTs are symmetric monoidal
          functors from <code>2Cob</code> (a dagger compact category of cobordisms) to{" "}
          <code>FdVect</code>.
        </li>
        <li>
          <strong>Signal processing and control theory</strong> — the category of finite-dimensional
          linear systems with the transpose dagger.
        </li>
      </ul>
    </>
  ),
  references: [
    {
      label: "Theorem Canon (ACCOUNT SDP — research direction for dagger extension)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Intuitionistic Linear Logic in CSK",
      path: "theory/structural/Intuitionistic-Linear-Logic-In-CSK.md",
    },
    {
      label: "Abramsky & Coecke (2004)",
      path: "https://arxiv.org/abs/quant-ph/0402130",
      note: "A categorical semantics of quantum protocols.",
    },
    {
      label: "Coecke & Kissinger (2017)",
      path: "https://doi.org/10.1017/9781316219317",
      note: "Picturing Quantum Processes — a first introduction to quantum theory using string diagrams.",
    },
  ],
};

export function DaggerCompactExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<DaggerCompactDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
